/**
 * Overview — what the current slice cost, where it went, and what moved.
 *
 * Reading order is deliberate: spend and its trend, then the two splits that
 * explain it (what kind of cost, which channel), then the data-quality strip.
 * The quality strip is last but not least — a cost dashboard's worst failure
 * is not an error, it is a model recorded at $0 that reads as thrift.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, Filler, Tooltip, Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { AlertTriangle, PhoneOff } from 'lucide-react';
import {
  AdminAPI, UsageBreakdown, UsageDimension, UsageFilterState,
  UsageReportSummary, UsageReportTimeseries, UsageSplit,
} from '../../lib/api';
import { CHANNEL_COLOR, CHANNEL_LABEL, Card, Chip, DIMENSION_LABEL, Empty, Kpi, MODALITY_COLOR, MODALITY_LABEL, Notice, ShareBar, Table, Td, Th, compact, int, minutes, pct, shortDate, Money, ExportCsv, downloadCsv, useCurrency } from './shared';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip, Legend);

export const OverviewTab: React.FC<{
  api: AdminAPI;
  filters: UsageFilterState;
  summary: UsageReportSummary | null;
  nonce: number;
  onDrill: (patch: Record<string, string | undefined>) => void;
}> = ({ api, filters, summary, nonce, onDrill }) => {
  const { fmt, unitRate } = useCurrency();
  const [split, setSplit] = useState<UsageSplit>('modality');
  const [series, setSeries] = useState<UsageReportTimeseries | null>(null);
  const [seriesError, setSeriesError] = useState<string | null>(null);
  const [dimension, setDimension] = useState<UsageDimension>('agent');
  const [breakdown, setBreakdown] = useState<UsageBreakdown | null>(null);
  const [breakdownError, setBreakdownError] = useState<string | null>(null);

  useEffect(() => {
    api.getUsageReportTimeseries(filters, split)
      .then((s) => { setSeries(s); setSeriesError(null); })
      .catch((e) => { setSeries(null); setSeriesError(e instanceof Error ? e.message : String(e)); });
  }, [api, filters, split, nonce]);

  useEffect(() => {
    api.getUsageBreakdown(filters, dimension, 12)
      .then((b) => { setBreakdown(b); setBreakdownError(null); })
      .catch((e) => { setBreakdown(null); setBreakdownError(e instanceof Error ? e.message : String(e)); });
  }, [api, filters, dimension, nonce]);

  const t = summary?.totals;
  const breakdownCols = dimension === 'model' ? 6 : 5;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          label="Total spend"
          value={<Money value={t?.cost_usd} inr={t?.cost_inr} />}
          accent="#6366f1"
          deltaPct={summary?.previous.cost_delta_pct ?? null}
          deltaIsGood={(d) => d <= 0}
          sub={`${int(t?.calls)} provider calls · ${int(t?.orgs)} organizations · ${int(t?.agents)} agents`}
          hint="Every priced event in the window: LLM, speech and carrier minutes."
        />
        <Kpi
          label="Cost per session · all-in"
          value={<Money value={t?.cost_per_session_allin} inr={t?.cost_per_session_allin_inr} />}
          accent="#0ea5e9"
          sub={`${int(t?.sessions)} sessions · ${fmt(t?.cost_per_session, t?.cost_per_session_inr)} before analysis`}
          hint="Conversation cost plus the analysis and compliance each one caused. Agent building and ingest are counted in the total but belong to no session."
        />
        <Kpi
          label="Cost per minute"
          value={<Money value={t?.cost_per_minute} inr={t?.cost_per_minute_inr} />}
          accent="#f59e0b"
          sub={`${minutes(t?.minutes)} measured · all-in: LLM + speech + carrier`}
          hint="Session cost divided by measured minutes. Simulator runs are excluded unless the filter includes them."
        />
        <Kpi
          label="Margin · all-in"
          value={t?.margin_allin_pct == null ? '—' : pct(t.margin_allin_pct)}
          accent="#10b981"
          sub={`${fmt(t?.revenue_usd)} billed − ${fmt(t?.allin_session_cost_usd, t?.allin_session_cost_inr)} all-in cost`}
          hint="What the rate cards bill for these sessions, less what serving and analysing them cost."
        />
      </div>

      {(summary?.fx || (summary?.simulated && summary.simulated.sessions > 0)) && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 px-1 text-[11px] text-gray-500">
          {summary?.fx && (
            <span title="Each day's spend is converted at that day's fixing, so a month's rupee total is not one rate times a dollar total.">
              ₹ figures use the {summary.fx.source === 'ecb' ? 'ECB' : summary.fx.source} mid-market rate for
              each day's spend · latest ₹{summary.fx.rate.toFixed(2)}/$ ({summary.fx.as_of})
            </span>
          )}
          {summary?.simulated && summary.simulated.sessions > 0 && (
            <span>
              Left out: {int(summary.simulated.sessions)} simulator sessions ({fmt(summary.simulated.cost_usd)}) —
              tick “Include simulator calls” to count them
            </span>
          )}
        </div>
      )}

      <ChannelStrip summary={summary} filters={filters} onDrill={onDrill} />

      <QualityStrip summary={summary} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card
          className="xl:col-span-2"
          title="Daily spend"
          subtitle="Dense series — a day with no spend is a bar of zero, not a gap"
          right={
            <div className="inline-flex overflow-hidden rounded border border-gray-300">
              {(['modality', 'channel', 'agent_kind'] as UsageSplit[]).map((s) => (
                <button
                  key={s}
                  onClick={() => setSplit(s)}
                  className={`px-2 py-1 text-[11px] ${split === s ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  {s === 'agent_kind' ? 'Agent type' : s === 'modality' ? 'Cost type' : 'Channel'}
                </button>
              ))}
            </div>
          }
        >
          {seriesError ? (
            <Notice tone="error">Couldn't load the daily series: {seriesError}. Refresh to try again.</Notice>
          ) : (
            <DailyChart series={series} split={split} />
          )}
        </Card>

        <Card
          title="Where the money goes"
          subtitle="By cost type"
          right={<ExportCsv onClick={() => downloadCsv(`cost_types_${filters.from}_${filters.to}`, [
            { key: 'modality', label: 'cost_type' }, { key: 'calls', label: 'calls' },
            { key: 'cost_usd', label: 'cost_usd' }, { key: 'input_tokens', label: 'input_tokens' },
            { key: 'output_tokens', label: 'output_tokens' }, { key: 'chars', label: 'chars' },
            { key: 'audio_seconds', label: 'audio_seconds' },
          ], (summary?.by_modality ?? []) as unknown as Record<string, unknown>[])} />}
        >
          <div className="space-y-3">
            {(summary?.by_modality ?? []).map((m) => {
              const share = t?.cost_usd ? (m.cost_usd / t.cost_usd) * 100 : 0;
              return (
                <button
                  key={m.modality}
                  onClick={() => onDrill({ modality: m.modality })}
                  className="w-full rounded px-1 py-1 text-left hover:bg-gray-50"
                >
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="flex items-center gap-1.5 text-gray-700">
                      <span className="inline-block h-2 w-2 rounded-full" style={{ background: MODALITY_COLOR[m.modality] }} />
                      {MODALITY_LABEL[m.modality]}
                    </span>
                    <span className="tabular-nums font-medium text-gray-900"><Money value={m.cost_usd} bare /></span>
                  </div>
                  <div className="mt-1"><ShareBar pct={share} color={MODALITY_COLOR[m.modality]} /></div>
                  <div className="mt-1 text-[11px] text-gray-500">
                    {int(m.calls)} calls · {unitFor(m)}
                  </div>
                </button>
              );
            })}
            {!summary?.by_modality.length && <Empty />}
          </div>
        </Card>
      </div>

      <ChannelCards summary={summary} onDrill={onDrill} />

      <Card
        title="Breakdown"
        subtitle="Click a row to filter the whole page to it"
        right={
          <div className="flex items-center gap-2">
            <select
              value={dimension}
              onChange={(e) => setDimension(e.target.value as UsageDimension)}
              className="rounded border-gray-300 py-1 text-xs"
            >
              {([
                'agent', 'campaign', 'customer', 'org', 'model',
                'provider', 'feature', 'channel', 'status',
              ] as UsageDimension[]).map((d) => (
                <option key={d} value={d}>By {DIMENSION_LABEL[d].toLowerCase()}</option>
              ))}
            </select>
            <ExportCsv onClick={() => downloadCsv(`breakdown_by_${dimension}_${filters.from}_${filters.to}`, [
              { key: 'label', label: dimension }, { key: 'key', label: 'key' }, { key: 'calls', label: 'calls' },
              { key: 'sessions', label: 'sessions' }, { key: 'cost_usd', label: 'cost_usd' }, { key: 'cost_inr', label: 'cost_inr' },
              { key: 'cost_per_session', label: 'cost_per_session_usd' }, { key: 'share_pct', label: 'share_pct' },
              { key: 'errors', label: 'errors' }, { key: 'input_tokens', label: 'input_tokens' },
              { key: 'output_tokens', label: 'output_tokens' }, { key: 'chars', label: 'chars' },
              { key: 'audio_seconds', label: 'audio_seconds' },
            ], (breakdown?.rows ?? []) as unknown as Record<string, unknown>[])} />
          </div>
        }
        bodyClassName=""
      >
        <Table
          head={
            <tr>
              <Th>{DIMENSION_LABEL[dimension]}</Th>
              <Th align="right">Calls</Th>
              <Th align="right">Sessions</Th>
              {dimension === 'model' && <Th align="right" title="The live rate this model's calls are priced at">Unit rate</Th>}
              <Th align="right">Cost</Th>
              <Th className="w-32">Share</Th>
            </tr>
          }
        >
          {(breakdown?.rows ?? []).map((r) => (
            <tr
              key={r.key ?? 'null'}
              className={`cursor-pointer hover:bg-gray-50 ${r.priced === false ? 'bg-rose-50/60' : ''}`}
              onClick={() => onDrill(drillFor(dimension, r.key))}
            >
              <Td>
                <span className="text-gray-900">{r.label}</span>
                {r.errors > 0 && (
                  <span className="ml-2 text-[11px] text-rose-600">{int(r.errors)} errors</span>
                )}
              </Td>
              <Td align="right" className="tabular-nums">{int(r.calls)}</Td>
              <Td align="right" className="tabular-nums">{int(r.sessions)}</Td>
              {dimension === 'model' && (
                <Td align="right" className="whitespace-nowrap tabular-nums text-xs">
                  {r.priced === false
                    ? <span className="font-medium text-rose-600">No rate — priced at zero</span>
                    : modelRate(r, unitRate)}
                </Td>
              )}
              <Td align="right" className="tabular-nums font-medium text-gray-900"><Money value={r.cost_usd} inr={r.cost_inr} /></Td>
              <Td>
                <div className="flex items-center gap-2">
                  <ShareBar pct={r.share_pct} />
                  <span className="w-10 shrink-0 text-right text-[11px] tabular-nums text-gray-500">
                    {r.share_pct.toFixed(0)}%
                  </span>
                </div>
              </Td>
            </tr>
          ))}
          {breakdownError && (
            <Empty colSpan={breakdownCols}>
              <span className="text-rose-700">Couldn't load the breakdown: {breakdownError}. Refresh to try again.</span>
            </Empty>
          )}
          {!breakdownError && breakdown === null && <Empty colSpan={breakdownCols}>Loading…</Empty>}
          {breakdown !== null && !breakdown.rows.length && (
            <Empty colSpan={breakdownCols}>Nothing in this window. Widen the range or clear a filter.</Empty>
          )}
        </Table>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------

const unitFor = (m: UsageReportSummary['by_modality'][number]): string => {
  if (m.modality === 'tts') return `${compact(m.chars)} characters`;
  if (m.modality === 'stt') return `${minutes(m.audio_seconds / 60)} audio`;
  if (m.modality === 'telephony') return `${minutes(m.audio_seconds / 60)} billed`;
  return `${compact(m.input_tokens)} → ${compact(m.output_tokens)} tokens`;
};

const modelRate = (
  r: { rate?: { input_per_mtok: number; output_per_mtok: number; per_million_chars: number | null; per_audio_minute: number | null } | null; sample_modality: string },
  unitRate: (usdValue: number | null | undefined, suffix: string) => string,
) => {
  if (r.sample_modality === 'telephony') return 'Carrier rate card';
  const x = r.rate;
  if (!x) return '—';
  if (x.per_million_chars != null) return unitRate(x.per_million_chars, '/1M chars');
  if (x.per_audio_minute != null) return unitRate(x.per_audio_minute, '/audio min');
  return `${unitRate(x.input_per_mtok, '')} / ${unitRate(x.output_per_mtok, '')} per 1M tokens`;
};

const drillFor = (dimension: UsageDimension, key: string | null): Record<string, string | undefined> => {
  if (!key) return {};
  switch (dimension) {
    case 'agent': return { agent: key };
    case 'org': return { org: key };
    case 'provider': return { provider: key };
    case 'feature': return { feature: key };
    case 'channel': return { channel: key };
    case 'modality': return { modality: key };
    case 'campaign': return { campaign: key };
    case 'customer': return { customer: key };
    default: return {};
  }
};

/**
 * Data-quality strip.
 *
 * Every item here is spend the page is currently under-reporting. They sit
 * above the fold on purpose: an unpriced model or an uncosted call does not
 * look like a problem anywhere else on a cost dashboard — it looks like a
 * smaller bill.
 */
const QualityStrip: React.FC<{ summary: UsageReportSummary | null }> = ({ summary }) => {
  const { fmt } = useCurrency();
  const items: React.ReactNode[] = [];
  if (summary?.unpriced_models?.length) {
    const top = summary.unpriced_models.slice(0, 3)
      .map((u) => `${u.provider}/${u.model}`).join(', ');
    items.push(
      <Notice key="models" tone="error" icon={<AlertTriangle className="h-3.5 w-3.5" />}>
        <strong>{summary.unpriced_models.length} model{summary.unpriced_models.length > 1 ? 's' : ''} with no rate</strong>{' '}
        ({top}{summary.unpriced_models.length > 3 ? ', …' : ''}) — their calls are recorded at zero,
        so the totals above are lower than the truth. Each needs a rate row added; existing rates are edited on the Model pricing page.
      </Notice>,
    );
  }
  if (summary?.uncosted_calls?.calls) {
    items.push(
      <Notice key="calls" tone="warn" icon={<PhoneOff className="h-3.5 w-3.5" />}>
        <strong>{int(summary.uncosted_calls.calls)} completed calls</strong> carry no carrier cost
        ({minutes(summary.uncosted_calls.seconds / 60)} of talk time). Calls are costed at hangup;
        these predate that, and are deliberately not restated at estimated rates.
      </Notice>,
    );
  }
  if (summary?.unattributed?.calls) {
    items.push(
      <Notice key="unattr" tone="warn" icon={<AlertTriangle className="h-3.5 w-3.5" />}>
        <strong>{fmt(summary.unattributed.cost_usd)}</strong> across {int(summary.unattributed.calls)} calls
        has no organization attribution. It is included in the totals above.
      </Notice>,
    );
  }
  if (!items.length) return null;
  return <div className="space-y-2">{items}</div>;
};

const DailyChart: React.FC<{ series: UsageReportTimeseries | null; split: UsageSplit }> = ({ series, split }) => {
  const { fmt, axis } = useCurrency();
  const data = useMemo(() => {
    const points = series?.points ?? [];
    const keys = series?.series_keys ?? [];
    const colorFor = (k: string) =>
      split === 'modality' ? (MODALITY_COLOR as Record<string, string>)[k] ?? '#94a3b8'
        : (CHANNEL_COLOR[k] ?? '#94a3b8');
    const labelFor = (k: string) =>
      split === 'modality' ? (MODALITY_LABEL as Record<string, string>)[k] ?? k
        : (CHANNEL_LABEL[k] ?? k);
    return {
      labels: points.map((p) => shortDate(p.day)),
      datasets: keys.map((k) => ({
        label: labelFor(k),
        data: points.map((p) => p.series[k] ?? 0),
        backgroundColor: colorFor(k),
        stack: 'cost',
      })),
    };
  }, [series, split]);

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } },
      tooltip: {
        callbacks: {
          label: (c: any) => `${c.dataset.label}: ${fmt(c.parsed.y ?? 0)}`,
          footer: (items: any[]) =>
            `Total: ${fmt(items.reduce((a, i) => a + (i.parsed.y ?? 0), 0))}`,
        },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { maxTicksLimit: 14, font: { size: 10 } } },
      y: {
        stacked: true, beginAtZero: true,
        ticks: { callback: (v: any) => axis(Number(v)), font: { size: 10 } },
      },
    },
  };

  return <div className="h-64"><Bar data={data} options={options} /></div>;
};

/**
 * Per-channel unit economics.
 *
 * The comparison the old page could not make at all: what a phone conversation
 * costs against what a screen conversation costs, on the same agents, over the
 * same window.
 */
const ChannelCards: React.FC<{
  summary: UsageReportSummary | null;
  onDrill: (patch: Record<string, string | undefined>) => void;
}> = ({ summary, onDrill }) => {
  const { fmt } = useCurrency();
  const rows = summary?.by_channel ?? [];
  if (!rows.length) return null;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {rows.map((c) => (
        <Card
          key={c.channel}
          title={<span className="flex items-center gap-2"><Chip kind="channel" value={c.channel} /> {CHANNEL_LABEL[c.channel] ?? c.channel}</span>}
          right={
            <button onClick={() => onDrill({ channel: c.channel })} className="text-blue-600 hover:underline">
              Filter
            </button>
          }
        >
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <Stat label="Sessions" value={int(c.sessions)} />
            <Stat label="Measured time" value={minutes(c.minutes)}
                  hint={c.duration_basis === 'metered'
                    ? 'Carrier-metered minutes for at least some sessions.'
                    : 'Engaged time, summed from per-turn wall clocks.'} />
            <Stat label="Cost / session" value={<Money value={c.cost_per_session} inr={c.cost_per_session_inr} />} />
            <Stat label="Cost / minute" value={<Money value={c.cost_per_minute} inr={c.cost_per_minute_inr} />} />
            <Stat label="Billed" value={`${int(c.credits_billable)} cr`}
                  hint="What the rate card charges for these sessions." />
            <Stat label="Margin" value={c.margin_pct == null ? '—' : pct(c.margin_pct)}
                  tone={c.margin_pct != null && c.margin_pct < 0 ? 'bad' : undefined} />
          </dl>
          <div className="mt-3 border-t border-gray-100 pt-2 text-[11px] text-gray-500">
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <span>LLM {fmt(c.llm_cost)}</span>
              <span>Speech out {fmt(c.tts_cost)}</span>
              <span>Speech in {fmt(c.stt_cost)}</span>
              {c.telephony_cost > 0 && <span className="text-amber-700">Carrier {fmt(c.telephony_cost)}</span>}
            </div>
            {c.non_session_units > 0 && (
              <div className="mt-1 text-gray-400">
                + {fmt(c.non_session_cost_usd)} of non-session work ({int(c.non_session_units)} units), excluded from the rates above
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string; tone?: 'bad' }> = ({ label, value, hint, tone }) => (
  <div title={hint}>
    <dt className="text-[11px] uppercase tracking-wide text-gray-400">{label}</dt>
    <dd className={`tabular-nums font-medium ${tone === 'bad' ? 'text-rose-600' : 'text-gray-900'}`}>{value}</dd>
  </div>
);


/**
 * The comparison at the top of the page: every channel on the unit it sells,
 * side by side. The Channels tab is the deep dive; this is the row of numbers
 * a pricing conversation starts from.
 */
const ChannelStrip: React.FC<{
  summary: UsageReportSummary | null;
  filters: UsageFilterState;
  onDrill: (patch: Record<string, string | undefined>) => void;
}> = ({ summary, filters, onDrill }) => {
  const { fmt, ccy, toDisplay } = useCurrency();
  const rows = (summary?.by_channel ?? []).filter((c) => c.channel !== 'system' && c.channel !== 'unknown');
  if (!rows.length) return null;
  const unitWord: Record<string, string> = { call: 'connected calls', session: 'sessions', message: 'messages' };
  const csv = () => downloadCsv(`channels_${filters.from}_${filters.to}`, [
    { key: 'channel', label: 'channel' }, { key: 'unit_label', label: 'unit' }, { key: 'units', label: 'units' },
    { key: 'rated_minutes', label: 'minutes' },
    { key: 'session_cost_usd', label: `cost_${ccy}`, get: (r) => toDisplay(r.session_cost_usd as number) },
    { key: 'allin_cost_usd', label: `cost_allin_${ccy}`, get: (r) => toDisplay(r.allin_cost_usd as number) },
    { key: 'cost_per_unit', label: `cost_per_unit_${ccy}`, get: (r) => r.cost_per_unit == null ? '' : toDisplay(r.cost_per_unit as number) },
    { key: 'allin_per_unit', label: `cost_per_unit_allin_${ccy}`, get: (r) => r.allin_per_unit == null ? '' : toDisplay(r.allin_per_unit as number) },
    { key: 'cost_per_minute', label: `cost_per_minute_${ccy}`, get: (r) => r.cost_per_minute == null ? '' : toDisplay(r.cost_per_minute as number) },
    { key: 'llm_per_minute', label: `llm_per_minute_${ccy}`, get: (r) => r.llm_per_minute == null ? '' : toDisplay(r.llm_per_minute as number) },
    { key: 'tts_per_minute', label: `tts_per_minute_${ccy}`, get: (r) => r.tts_per_minute == null ? '' : toDisplay(r.tts_per_minute as number) },
    { key: 'stt_per_minute', label: `stt_per_minute_${ccy}`, get: (r) => r.stt_per_minute == null ? '' : toDisplay(r.stt_per_minute as number) },
    { key: 'telephony_per_minute', label: `carrier_per_minute_${ccy}`, get: (r) => r.telephony_per_minute == null ? '' : toDisplay(r.telephony_per_minute as number) },
    { key: 'overhead_per_unit', label: `analysis_per_unit_${ccy}`, get: (r) => r.overhead_per_unit == null ? '' : toDisplay(r.overhead_per_unit as number) },
    { key: 'revenue_per_unit', label: `revenue_per_unit_${ccy}`, get: (r) => r.revenue_per_unit == null ? '' : toDisplay(r.revenue_per_unit as number) },
    { key: 'margin_per_unit', label: `margin_per_unit_${ccy}`, get: (r) => r.margin_per_unit == null ? '' : toDisplay(r.margin_per_unit as number) },
    { key: 'margin_pct', label: 'margin_pct' },
  ], rows as unknown as Record<string, unknown>[]);
  return (
    <Card
      title="Channel economics"
      subtitle="Each channel on the unit it sells: a connected call, a session, a message. Rates are all-in over that channel's own clock."
      right={<ExportCsv onClick={csv} />}
      bodyClassName=""
    >
      <Table
        head={
          <tr>
            <Th>Channel</Th>
            <Th align="right">Units</Th>
            <Th align="right">Minutes</Th>
            <Th align="right" title="Conversation plus the analysis it caused">COGS · all-in</Th>
            <Th align="right" title="All-in per connected call / session / message">COGS / unit</Th>
            <Th align="right" title="Talk-time rate: LLM + speech + carrier. Analysis is per unit, not per minute, so it is not in here">Rate / min</Th>
            <Th className="w-40" title="The per-minute rate, by what it is made of">Made of</Th>
            <Th align="right" title="Post-conversation analysis, charged once per unit — part of COGS / unit above">of which analysis</Th>
            <Th align="right">Revenue / unit</Th>
            <Th align="right">Margin</Th>
          </tr>
        }
      >
        {rows.map((c) => {
          const parts: [string, number | null][] = [['llm', c.llm_per_minute], ['tts', c.tts_per_minute], ['stt', c.stt_per_minute], ['telephony', c.telephony_per_minute]];
          const tot = parts.reduce((a, [, v]) => a + (v ?? 0), 0);
          return (
            <tr key={c.channel} className="cursor-pointer hover:bg-gray-50" onClick={() => onDrill({ channel: c.channel })}>
              <Td><div className="flex items-center gap-2"><Chip kind="channel" value={c.channel} /><span className="text-gray-900">{CHANNEL_LABEL[c.channel] ?? c.channel}</span></div></Td>
              <Td align="right" className="tabular-nums">{int(c.units)}<div className="text-[10px] font-normal text-gray-400">{unitWord[c.unit_label] ?? c.unit_label}</div></Td>
              <Td align="right" className="tabular-nums">{minutes(c.rated_minutes)}<div className="text-[10px] font-normal text-gray-400">{c.channel === 'phone' ? 'carrier talk' : 'engaged'}</div></Td>
              <Td align="right" className="tabular-nums"><Money value={c.allin_cost_usd} inr={c.allin_cost_inr} /></Td>
              <Td align="right" className="tabular-nums font-medium text-gray-900"><Money value={c.allin_per_unit} inr={c.allin_per_unit_inr} /></Td>
              <Td align="right" className="tabular-nums font-medium text-gray-900"><Money value={c.cost_per_minute} inr={c.cost_per_minute_inr} /></Td>
              <Td>
                {tot > 0 && (
                  <div className="flex h-2.5 w-full overflow-hidden rounded" title={parts.map(([k, v]) => `${MODALITY_LABEL[k as keyof typeof MODALITY_LABEL]} ${fmt(v, undefined, 4)}/min`).join(' · ')}>
                    {parts.map(([k, v]) => (v ?? 0) > 0 && <div key={k} style={{ width: `${((v ?? 0) / tot) * 100}%`, background: MODALITY_COLOR[k as keyof typeof MODALITY_COLOR] }} />)}
                  </div>
                )}
              </Td>
              <Td align="right" className="tabular-nums text-gray-600"><Money value={c.overhead_per_unit} inr={c.overhead_per_unit_inr} bare /></Td>
              <Td align="right" className="tabular-nums text-gray-700"><Money value={c.revenue_per_unit} bare /></Td>
              <Td align="right" className={`tabular-nums font-medium ${c.margin_allin_pct == null ? 'text-gray-400' : c.margin_allin_pct < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                {c.margin_allin_pct == null ? '—' : pct(c.margin_allin_pct)}
                <div className="text-[10px] font-normal text-gray-400"><Money value={c.margin_per_unit} bare /> / unit</div>
              </Td>
            </tr>
          );
        })}
      </Table>
    </Card>
  );
};
